import React from "react";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import SyncIcon from "@mui/icons-material/Sync";
import DescriptionIcon from "@mui/icons-material/Description";
import DownloadIcon from "@mui/icons-material/Download";
import AddIcon from "@mui/icons-material/Add";
import BuildIcon from "@mui/icons-material/Build";
import { PRIVILEGES } from "../../api/types/dtos";
import { useHasPrivilege } from "../../components/authorization";

export interface MenuActions {
  reportFromFile: () => void;
  reportFromTemplate: () => void;
  reportBuilder: () => void;
  syncItems: () => void;
  createChallenge: () => void;
}

export interface CsvHandlers {
  downloadCsv: () => void;
  downloadTemplates: () => void;
  downloadNames: () => void;
  downloadFullExport: () => void;
  downloadOptions: () => void;
}

interface ChallengesActionsMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onOpen: (el: HTMLElement) => void;
  onClose: () => void;
  menuActions: MenuActions;
  csvHandlers: CsvHandlers;
  isGeneratingReport: boolean;
  hasChallenges: boolean;
  hasProgramVersionItems: boolean;
  templateDownloadEnabled: boolean;
}

const ChallengesActionsMenu: React.FC<ChallengesActionsMenuProps> = ({
  anchorEl,
  open,
  onOpen,
  onClose,
  menuActions,
  csvHandlers,
  isGeneratingReport,
  hasChallenges,
  hasProgramVersionItems,
  templateDownloadEnabled,
}) => {
  const { hasPrivilege: canCreateReport } = useHasPrivilege(PRIVILEGES.CREATE_REPORT);
  const { hasPrivilege: canCreateTemplate } = useHasPrivilege(PRIVILEGES.CREATE_REPORT_TEMPLATE);
  const { hasPrivilege: canUpdateInspection } = useHasPrivilege(PRIVILEGES.UPDATE_INSPECTION);
  const { hasPrivilege: canCreateInspection } = useHasPrivilege(PRIVILEGES.CREATE_INSPECTION);

  return (
    <>
      <Button
        variant="outlined"
        endIcon={<KeyboardArrowDownIcon />}
        startIcon={<BuildIcon />}
        onClick={(e) => onOpen(e.currentTarget)}
      >
        Acciones
      </Button>
      <Menu anchorEl={anchorEl} open={open} onClose={onClose}>
        {canCreateReport && (
          <MenuItem onClick={menuActions.reportFromFile} disabled={isGeneratingReport}>
            <ListItemIcon>
              {isGeneratingReport ? (
                <CircularProgress size={20} />
              ) : (
                <UploadFileIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText>
              {isGeneratingReport ? "Generando..." : "Reporte desde archivo"}
            </ListItemText>
          </MenuItem>
        )}
        {canCreateReport && (
          <MenuItem onClick={menuActions.reportFromTemplate}>
            <ListItemIcon><DescriptionIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Reporte desde plantilla</ListItemText>
          </MenuItem>
        )}
        {canCreateTemplate && (
          <MenuItem onClick={menuActions.reportBuilder}>
            <ListItemIcon><BuildIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Constructor de Reportes</ListItemText>
          </MenuItem>
        )}
        {(canCreateReport || canCreateTemplate) && <Divider />}
        {canUpdateInspection && (
          <MenuItem onClick={menuActions.syncItems}>
            <ListItemIcon><SyncIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Sincronizar Items</ListItemText>
          </MenuItem>
        )}
        {canCreateInspection && (
          <MenuItem onClick={menuActions.createChallenge}>
            <ListItemIcon><AddIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Crear Reto</ListItemText>
          </MenuItem>
        )}
        {(canUpdateInspection || canCreateInspection) && <Divider />}
        <MenuItem onClick={csvHandlers.downloadCsv} disabled={!hasChallenges}>
          <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Descargar CSV</ListItemText>
        </MenuItem>
        {templateDownloadEnabled && (
          <>
            <MenuItem onClick={csvHandlers.downloadTemplates} disabled={!hasProgramVersionItems}>
              <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Descargar Plantillas</ListItemText>
            </MenuItem>
            <MenuItem onClick={csvHandlers.downloadNames} disabled={!hasProgramVersionItems}>
              <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Descargar Nombres de Retos</ListItemText>
            </MenuItem>
            <MenuItem onClick={csvHandlers.downloadOptions} disabled={!hasProgramVersionItems}>
              <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Descargar Opciones</ListItemText>
            </MenuItem>
            <MenuItem onClick={csvHandlers.downloadFullExport} disabled={!hasChallenges}>
              <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Exportar Todo</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>
    </>
  );
};

export default ChallengesActionsMenu;
